import { combineReducers } from 'redux';
import {
  FETCH_ASSESSMENT_ITEMS_STARTED,
  FETCH_ASSESSMENT_ITEMS_SUCCESS,
  FETCH_ASSESSMENT_ITEMS_FAILED,
  MAP_ASSIGNMENTS_SUCCESS,
  MAP_ASSIGNMENTS_STARTED,
  MAP_ASSIGNMENTS_FAILED,
  MAP_ASSIGNMENTS_CLEAR,
  DOWNLOAD_CSV_STARTED,
  DOWNLOAD_CSV_SUCCESS,
  DOWNLOAD_CSV_FAILED,
  EXPORT_GRADES_STARTED,
  EXPORT_GRADES_SUCCESS,
  EXPORT_GRADES_FAILED,
  EXPORT_GRADES_EVENT_START,
  FETCH_AI_STUDENT_GRADES_STARTED,
  FETCH_AI_STUDENT_GRADES_SUCCESS,
  FETCH_AI_STUDENT_GRADES_FAILED,
  UPDATE_AI_STUDENT_GRADES_SUCCESS,
  FETCH_ALL_CALCULATED_GRADES_STARTED,
  FETCH_ALL_CALCULATED_GRADES_SUCCESS,
  FETCH_ALL_CALCULATED_GRADES_FAILED,
} from './assessmentItems.types';

import { CHANGE_CURRENT_SPACE } from '$data/core/core.types';
import updateStudentGradesInAssessmentItems from './utils/updateStudentGradesInAssessmentItems';

const statusReducer = (
  state = { isLoading: false, error: null },
  { type, error }
) => {
  switch (type) {
    case FETCH_ASSESSMENT_ITEMS_STARTED:
      return {
        isLoading: true,
        error: null,
      };
    case FETCH_ASSESSMENT_ITEMS_SUCCESS:
      return {
        ...state,
        isLoading: false,
      };
    case FETCH_ASSESSMENT_ITEMS_FAILED:
      return {
        isLoading: true,
        error,
      };
    default:
      return state;
  }
};

const byIdReducer = (
  state = {},
  {
    type,
    assessmentItems,
    assessmentItemId,
    assignmentMappings,
    job,
    studentGrades,
  }
) => {
  if (type === FETCH_ASSESSMENT_ITEMS_SUCCESS) {
    return assessmentItems.reduce((accumulatedItems, item) => {
      // retain studentGrades
      const { studentGrades: itemStudentGrades } = state[item.id] || {};

      return {
        ...accumulatedItems,
        [item.id]: {
          ...item,
          ...(itemStudentGrades && { studentGrades: itemStudentGrades }),
        },
      };
    }, {});
  }

  if (type === MAP_ASSIGNMENTS_SUCCESS) {
    return {
      ...state,
      [assessmentItemId]: {
        ...state[assessmentItemId],
        mappedAssignments: assignmentMappings,
      },
    };
  }

  if (type === EXPORT_GRADES_SUCCESS) {
    return {
      ...state,
      [job.assessmentItemId]: {
        ...state[job.assessmentItemId],
        lastJob: {
          id: job.id,
          status: job.status,
          educator: job.educator,
          updatedAt: job.updatedAt,
          createdAt: job.createdAt,
        },
      },
    };
  }

  if (type === EXPORT_GRADES_EVENT_START) {
    return {
      ...state,
      [job.assessmentItemId]: {
        ...state[job.assessmentItemId],
        lastJob: {
          id: job.id,
          status: job.status,
        },
      },
    };
  }

  if (
    type === FETCH_AI_STUDENT_GRADES_SUCCESS ||
    type === UPDATE_AI_STUDENT_GRADES_SUCCESS
  ) {
    const updatedAssessmentItems = Object.keys(studentGrades)
      .map((key) => ({
        ...state[key],
        ...(studentGrades[key] && {
          studentGrade: studentGrades[key],
          studentGrades: updateStudentGradesInAssessmentItems({
            studentGrades: state[key].studentGrades,
            updatedStudentGrade: studentGrades[key],
          }),
        }),
      }))
      .reduce(
        (accumulatedItems, item) => ({
          ...accumulatedItems,
          [item.id]: item,
        }),
        {}
      );

    return {
      ...state,
      ...updatedAssessmentItems,
    };
  }

  if (type === FETCH_ALL_CALCULATED_GRADES_SUCCESS) {
    return {
      ...state,
      [assessmentItemId]: {
        ...state[assessmentItemId],
        studentGrades,
      },
    };
  }

  // reset on changing space
  if (type === CHANGE_CURRENT_SPACE) {
    return {};
  }

  return state;
};

const mapAssignmentsReducer = (
  state = {
    isLoading: false,
    error: null,
    isSuccessful: false,
  },
  { type, error }
) => {
  switch (type) {
    case MAP_ASSIGNMENTS_STARTED:
      return {
        isLoading: true,
        error: null,
        isSuccessful: false,
      };
    case MAP_ASSIGNMENTS_SUCCESS:
      return {
        isLoading: false,
        isSuccessful: true,
        error: null,
      };
    case MAP_ASSIGNMENTS_FAILED:
      return {
        isLoading: false,
        isSuccessful: false,
        error,
      };
    case MAP_ASSIGNMENTS_CLEAR:
      return {
        isLoading: false,
        isSuccessful: false,
        error: null,
      };
    default:
      return state;
  }
};

const idsReducer = (state = [], { type, assessmentItems }) => {
  if (type === FETCH_ASSESSMENT_ITEMS_SUCCESS) {
    return assessmentItems.map((item) => item.id);
  }

  if (type === CHANGE_CURRENT_SPACE) {
    return [];
  }

  return state;
};

const downloadCsvReducer = (
  state = {
    isGeneratingCsv: false,
    error: null,
  },
  { type, error }
) => {
  switch (type) {
    case DOWNLOAD_CSV_STARTED:
      return {
        isGeneratingCsv: true,
        error: null,
      };
    case DOWNLOAD_CSV_SUCCESS:
      return {
        isGeneratingCsv: false,
        error: null,
      };
    case DOWNLOAD_CSV_FAILED:
      return {
        isGeneratingCsv: false,
        error,
      };
    default:
      return state;
  }
};

const exportGradesStatusReducer = (
  state = { isInitiatingExport: false, error: null },
  { type, error }
) => {
  switch (type) {
    case EXPORT_GRADES_STARTED:
      return {
        isInitiatingExport: true,
        error: null,
      };
    case EXPORT_GRADES_SUCCESS:
      return {
        isInitiatingExport: false,
        error: null,
      };
    case EXPORT_GRADES_FAILED:
      return {
        isInitiatingExport: false,
        error,
      };
    default:
      return state;
  }
};

const fetchGradesStatusReducer = (
  state = {
    isFetching: false,
    error: null,
  },
  { type, error }
) => {
  switch (type) {
    case FETCH_AI_STUDENT_GRADES_STARTED:
    case FETCH_ALL_CALCULATED_GRADES_STARTED:
      return {
        isFetching: true,
        error: null,
      };
    case FETCH_AI_STUDENT_GRADES_SUCCESS:
    case FETCH_ALL_CALCULATED_GRADES_SUCCESS:
      return {
        isFetching: false,
        error: null,
      };
    case FETCH_AI_STUDENT_GRADES_FAILED:
    case FETCH_ALL_CALCULATED_GRADES_FAILED:
      return {
        isFetching: false,
        error,
      };
    case CHANGE_CURRENT_SPACE:
      return {
        isFetching: false,
        error: null,
      };
    default:
      return state;
  }
};

export default combineReducers({
  status: statusReducer,
  byId: byIdReducer,
  ids: idsReducer,
  mapAssignmentsStatus: mapAssignmentsReducer,
  downloadCsvStatus: downloadCsvReducer,
  exportGradesStatus: exportGradesStatusReducer,
  fetchGradesStatus: fetchGradesStatusReducer,
});
